import { AnimatedPlaceholder } from '@/components/react/form/elements/AnimatedPlaceholder';
import { ErrorMessage } from '@/components/react/form/elements/ErrorMessage';
import { useFieldContext } from '@/components/react/form/hooks/form';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/react/ui/select';
import { type AstroLocale } from '@/schemas/components/shared/i18n';
import { useStore } from '@tanstack/react-form';
import { useState } from 'react';

export interface DropDownItem {
  valueOverride?: string;
  twoLetterCode?: string;
  flag?: string;
  en?: string;
  fr?: string;
  de?: string;
  es?: string;
  nl?: string;
  zh?: string;
}

interface SelectDropdownProps {
  items: DropDownItem[];
  placeholder?: string;
  language: AstroLocale;
}

export const SelectDropdown = ({
  items,
  placeholder,
  language,
}: SelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { name, state, handleChange, store } = useFieldContext<string>();
  const { errors } = useStore(store, ({ meta }) => meta);

  return (
    <div
      className={cn(`flex h-20 w-full flex-col gap-1`, {
        '@sm:basis-1/2': name === 'country',
      })}
    >
      <Select
        open={isOpen}
        onOpenChange={setIsOpen}
        onValueChange={(value) => handleChange(value)}
        value={state.value}
        defaultValue={state.value}
      >
        <div className="relative">
          <SelectTrigger
            aria-invalid={errors.length > 0}
            aria-labelledby={`${name}`}
            className={cn(
              `peer border-brand-medium-gray peer text-brand-purple-black [&_svg]:text-brand-purple data-[state=open]:border-brand-purple-bright focus-visible:border-brand-purple-bright focus-visible:ring-brand-focus aria-invalid:border-brand-error [&_svg:size-4 relative flex h-11 w-full items-center justify-between gap-2 rounded-sm border bg-transparent px-3 py-2 text-base font-medium whitespace-nowrap transition-opacity outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0`,
              {
                'ring-brand-focus ring-2': isOpen,
              },
            )}
          >
            <SelectValue />
          </SelectTrigger>

          {placeholder && !state.value && (
            <AnimatedPlaceholder
              placeholder={placeholder}
              variant="selectInput"
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          )}
        </div>
        <SelectContent className="border-brand-purple-black-10 border bg-white px-0">
          {items.map((item) => {
            const itemName = item[language];
            if (!itemName) return null;
            const value = item.valueOverride ?? itemName;

            return (
              <SelectItem
                key={itemName}
                value={value}
                className="focus:bg-brand-purple-light text-brand-purple-black relative flex w-full cursor-default items-center gap-2 rounded-sm px-4 py-2 pr-8 pl-2 text-base font-medium outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2"
              >
                {itemName}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <ErrorMessage />
    </div>
  );
};
